'use client';

import { Button } from '@/components/ui/button';
import { CardContent } from '@/components/ui/card';
import { useResendVerificationEmail } from '@/hooks/useResendVerificationEmail';
import { errorHandler } from '@/lib/errorHandler';
import { useRouter } from 'next/navigation';
import { useCallback } from 'react';
import { toast } from 'sonner';

interface EmailVerificationProps {
  userEmail: string;
  onBack?: () => void;
  showGoBack?: boolean;
}

export const EmailVerification = ({
  userEmail,
  onBack,
  showGoBack = true,
}: EmailVerificationProps) => {
  const router = useRouter();
  const { mutate: resendVerificationEmail, isPending: isLoading } =
    useResendVerificationEmail();

  const handleResendVerificationEmail = useCallback(() => {
    resendVerificationEmail(userEmail, {
      onSuccess: () => {
        toast.success('Verification email resent');
      },
      onError: (error) => {
        toast.error(errorHandler(error));
      },
    });
  }, [userEmail, resendVerificationEmail]);

  const handleGoBack = useCallback(() => {
    if (onBack) {
      onBack();
    } else {
      router.back();
    }
  }, [onBack, router]);

  return (
    <CardContent className="space-y-4 flex items-center justify-center flex-col">
      <p className="text-center text-sm text-[#24292E]">
        Check your email for a verification link to activate your account.
      </p>
      <div className="flex items-center justify-center gap-2">
        {showGoBack && (
          <Button
            variant={'secondary'}
            onClick={handleGoBack}
            className="uppercase font-bold"
          >
            Go back
          </Button>
        )}
        <Button
          onClick={handleResendVerificationEmail}
          className="uppercase font-bold"
          isLoading={isLoading}
        >
          Resend email
        </Button>
      </div>
    </CardContent>
  );
};
